import React from 'react';
import { ReachAssessment, ReachScore } from '../../../services/reachService';

const STAGES = ['r', 'e', 'a', 'c', 'h'] as const;
const STAGE_LABELS: Record<string, string> = { r: 'Recognise', e: 'Engage', a: 'Acquire', c: 'Capability', h: 'Habits' };
const ASSESSMENT_TYPES = ['champion', 'executive', 'practice-leader', 'user', 'general'] as const;
const TYPE_LABELS: Record<string, string> = {
  champion: 'Champion', executive: 'Executive', 'practice-leader': 'Practice Leader', user: 'End User', general: 'General'
};

function avg(values: (number | undefined)[]): number | null {
  const valid = values.filter(v => v !== undefined) as number[];
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

function scoreColor(score: number | null): string {
  if (score === null) return 'bg-slate-100 text-slate-400';
  if (score < 1.5) return 'bg-red-100 text-red-700';
  if (score < 2.5) return 'bg-amber-100 text-amber-700';
  if (score < 3.5) return 'bg-blue-100 text-blue-700';
  return 'bg-green-100 text-green-700';
}

interface Props {
  assessments: ReachAssessment[];
  layer: 'individual' | 'organisational';
}

export const ResultsHeatMap: React.FC<Props> = ({ assessments, layer }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-[#e2e8f0]">
            <th className="text-left py-2 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-36">Segment</th>
            {STAGES.map(s => (
              <th key={s} className="py-2 px-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">
                {STAGE_LABELS[s]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e2e8f0]">
          {ASSESSMENT_TYPES.map(type => {
            const typeAssessments = assessments.filter(a => a.type === type);
            if (!typeAssessments.length) return null;
            return (
              <tr key={type} className="hover:bg-slate-50/50">
                <td className="py-3 px-3 text-xs font-semibold text-navy">{TYPE_LABELS[type]}<br/>
                  <span className="font-normal text-slate-400">{typeAssessments.length} respondent{typeAssessments.length !== 1 ? 's' : ''}</span>
                </td>
                {STAGES.map(stage => {
                  const scores = typeAssessments.map(a => a[layer][stage] as ReachScore | undefined);
                  const score = avg(scores as (number | undefined)[]);
                  const color = scoreColor(score);
                  return (
                    <td key={stage} className="py-3 px-3 text-center">
                      <span className={`inline-flex items-center justify-center w-12 h-8 rounded-lg text-xs font-bold ${color}`}>
                        {score !== null ? score.toFixed(1) : '—'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!assessments.length && (
        <div className="py-12 text-center text-slate-400 text-sm">No assessments submitted yet.</div>
      )}
    </div>
  );
};
